
class ToolDurationCapability {
  constructor(planStateSetters, planGetters, savePlan) {
    this.setters = planStateSetters;
    this.getters = planGetters;
    this.savePlan = savePlan;
  }

  async handleToolDurationEditing(data) {
    console.log("🔧 ToolDurationCapability: Tool duration editing started:", data);
    const { 
      programIndex: targetProgramIndex, 
      stage: targetStage, 
      tool: targetToolName,
      toolName: alternateToolName,
      newDuration: updatedDuration,
      duration: alternateDuration // Support both field names
    } = data;

    // Use tool or toolName, whichever is provided
    const actualToolName = targetToolName || alternateToolName;
    const actualDuration = updatedDuration || alternateDuration;

    const finalProgramSequence = this.getters.finalProgramSequence;
    console.log("🔧 Current finalProgramSequence length:", finalProgramSequence?.length);

    // If no stage is provided, try to find the tool in any stage of the program
    let actualStage = targetStage;
    if (!actualStage && finalProgramSequence?.[targetProgramIndex]?.toolsByStage) {
      // Search for the tool in all stages
      const program = finalProgramSequence[targetProgramIndex];
      for (const [stageName, stageData] of Object.entries(program.toolsByStage)) {
        if (stageData.tools?.some(tool => tool.tool === actualToolName)) {
          actualStage = stageName;
          console.log("🔧 Found tool in stage:", actualStage);
          break;
        }
      }
    }

    // Validate input data
    if (targetProgramIndex === undefined || !actualStage || !actualToolName || actualDuration === undefined) {
      console.error("❌ Invalid tool duration editing data:", { 
        targetProgramIndex, 
        actualStage, 
        actualToolName, 
        actualDuration,
        originalData: data 
      });
      return { success: false, message: "Invalid data provided" };
    }

    // Find current duration for logging
    let currentDuration = "unknown";
    const targetProgram = finalProgramSequence[targetProgramIndex];
    if (targetProgram?.toolsByStage?.[actualStage]?.tools) {
      const existingTool = targetProgram.toolsByStage[actualStage].tools.find(t => t.tool === actualToolName);
      if (existingTool) {
        currentDuration = existingTool.duration || "unknown";
      }
    }

    console.log(`🔧 BEFORE UPDATE: ${actualToolName} duration: ${currentDuration} → ${actualDuration} hours`);
    console.log(`🔧 Target: Program[${targetProgramIndex}].${actualStage}`);

    // Helper function to update program tools duration
    const updateProgramToolDuration = (program) => {
      console.log(`🔧 Updating program: ${program?.program_of_work}`);

      if (!program?.toolsByStage?.[actualStage]?.tools) {
        console.warn(`🔧 Stage ${actualStage} not found in program ${program?.program_of_work}`);
        return program;
      }

      const toolIndex = program.toolsByStage[actualStage].tools.findIndex(t => t.tool === actualToolName);
      if (toolIndex === -1) {
        console.warn(`🔧 Tool ${actualToolName} not found in stage ${actualStage}`);
        return program;
      }

      console.log(`🔧 Found tool at index ${toolIndex} in stage ${actualStage}`);

      // Create deep copy to avoid mutations
      const updatedProgram = { ...program };
      updatedProgram.toolsByStage = { ...program.toolsByStage };
      updatedProgram.toolsByStage[actualStage] = { ...program.toolsByStage[actualStage] };
      updatedProgram.toolsByStage[actualStage].tools = [...program.toolsByStage[actualStage].tools];

      // Store old duration for comparison
      const oldToolDuration = updatedProgram.toolsByStage[actualStage].tools[toolIndex].duration;

      // Update the specific tool duration
      updatedProgram.toolsByStage[actualStage].tools[toolIndex] = {
        ...updatedProgram.toolsByStage[actualStage].tools[toolIndex],
        duration: actualDuration
      };

      console.log(`🔧 Tool duration updated: ${oldToolDuration} → ${actualDuration}`);

      // Recalculate stage total duration
      const oldStageDuration = updatedProgram.toolsByStage[actualStage].totalDuration;
      updatedProgram.toolsByStage[actualStage].totalDuration = 
        updatedProgram.toolsByStage[actualStage].tools.reduce((sum, tool) => sum + (tool.duration || 0), 0);

      console.log(`🔧 Stage ${actualStage} duration: ${oldStageDuration} → ${updatedProgram.toolsByStage[actualStage].totalDuration}`);

      // Recalculate program total duration
      const oldProgramDuration = updatedProgram.totalProgramLength;
      updatedProgram.totalProgramLength = Object.values(updatedProgram.toolsByStage)
        .reduce((sum, stageData) => sum + (stageData.totalDuration || 0), 0);

      console.log(`🔧 Program ${updatedProgram.program_of_work} duration: ${oldProgramDuration} → ${updatedProgram.totalProgramLength}`);

      return updatedProgram;
    };

    let updatedSequence = null;
    let newTotalDuration = 0;
    let updateSuccess = false;

    // Update finalProgramSequence and capture the updated sequence
    console.log("🔧 Updating finalProgramSequence...");
    this.setters.setFinalProgramSequence(prevSequence => {
      console.log("🔧 setFinalProgramSequence callback executing...");
      updatedSequence = [...prevSequence];
      const oldProgram = updatedSequence[targetProgramIndex];
      updatedSequence[targetProgramIndex] = updateProgramToolDuration(updatedSequence[targetProgramIndex]);

      // Verify the update was successful
      if (updatedSequence[targetProgramIndex] !== oldProgram) {
        updateSuccess = true;
        console.log("🔧 ✅ Program object reference changed - update successful");
      }

      // Calculate total duration from the updated sequence
      const oldTotalDuration = prevSequence.reduce((sum, program) => 
        sum + (program.totalProgramLength || 0), 0
      );
      newTotalDuration = updatedSequence.reduce((sum, program) => 
        sum + (program.totalProgramLength || 0), 0
      );

      console.log(`🔧 Total duration change: ${oldTotalDuration} → ${newTotalDuration}`);
      console.log("🔧 Updated finalProgramSequence callback complete");

      return updatedSequence;
    });

    // Update selectedPrograms with the same data
    console.log("🔧 Updating selectedPrograms...");
    this.setters.setSelectedPrograms(prevSelected => {
      console.log("🔧 setSelectedPrograms callback executing...");
      const updatedSelected = [...prevSelected];
      if (updatedSelected[targetProgramIndex]) {
        updatedSelected[targetProgramIndex] = updateProgramToolDuration(updatedSelected[targetProgramIndex]);
        console.log("🔧 selectedPrograms updated");
      } else {
        console.warn(`🔧 Program at index ${targetProgramIndex} not found in selectedPrograms`);
      }
      return updatedSelected;
    });

    // Update total duration with the calculated value
    console.log(`🔧 Updating total duration to: ${newTotalDuration}`);
    this.setters.setTotalDuration(newTotalDuration);

    // Mark as unsaved to trigger auto-save
    console.log("🔧 Marking plan as unsaved");
    this.setters.setIsSaved(false);

    // Force a save after a delay to ensure persistence
    setTimeout(() => {
      console.log("🔧 Triggering delayed save after tool duration update");
      if (this.savePlan && typeof this.savePlan === 'function') {
        this.savePlan();
      }
    }, 1000);

    const result = {
      success: updateSuccess,
      message: `${actualToolName} duration updated to ${actualDuration} hours`,
      updatedProgram: updatedSequence?.[targetProgramIndex],
      newTotalDuration,
      debugInfo: {
        targetProgramIndex,
        targetStage: actualStage,
        targetToolName: actualToolName,
        oldDuration: currentDuration,
        newDuration: actualDuration,
        updateSuccess
      }
    };

    console.log(`🔧 ✅ Tool duration update complete:`, result);

    return result;
  }

  
}

module.exports = ToolDurationCapability;
